/**
 * Splits the rendered itinerary into its own sections.
 *
 * Everything arrives as one blob of markdown from Notion, so costs and photos
 * land wherever they happen to sit on the page — which on a phone means
 * scrolling past a cost table to get back to Tuesday. This lifts those blocks
 * out of the itinerary body and into their own collapsible sections after
 * render.
 *
 * Done in the browser rather than in the sync Lambda so it applies to trips
 * already in the database, without waiting for a re-sync.
 */

/**
 * Drops the "add a Paid by column to this Notion database" note that older
 * syncs baked into the markdown.
 *
 * The renderer no longer emits it, but markdown is generated at sync time and
 * then stored, so every trip synced before that change still carries the
 * sentence and would keep showing it until someone re-synced. Removing it here
 * clears it from existing trips straight away, and does nothing once they are.
 *
 * Matched on the class rather than the wording, so every past phrasing goes.
 */
function stripStaleCostHints(content: HTMLElement): void {
  content.querySelectorAll('.notion-costs-hint').forEach((el) => el.remove());
}

/** Moves a node into `target`, preserving document order among what came before. */
function relocate(node: Node, target: HTMLElement): void {
  target.appendChild(node);
}

/**
 * Cost blocks, plus the database heading immediately above one.
 *
 * The heading is a sibling rather than a child — renderCostTable emits it
 * outside the wrapper — so leaving it behind would strand a title above an
 * empty gap in the itinerary.
 */
function extractCosts(content: HTMLElement, target: HTMLElement): number {
  const blocks = Array.from(content.querySelectorAll<HTMLElement>('.notion-costs'));
  for (const block of blocks) {
    const heading = block.previousElementSibling;
    if (heading?.classList.contains('notion-costs-title')) relocate(heading, target);
    relocate(block, target);
  }
  return blocks.length;
}

/**
 * Photos and other images from the page body.
 *
 * Deliberately not every <img>: callout icons and link-preview thumbnails are
 * chrome belonging to the block around them, and pulling those out would gut
 * the itinerary rather than tidy it.
 */
function extractAttachments(content: HTMLElement, target: HTMLElement): number {
  const images = Array.from(content.querySelectorAll<HTMLImageElement>('img')).filter(
    (img) => !img.classList.contains('notion-icon-img') && !img.closest('.notion-bookmark'),
  );

  for (const img of images) {
    const figure = document.createElement('figure');
    figure.className = 'trip-attachment';

    // A markdown image sits alone in its own paragraph. Move that paragraph so
    // no empty <p> is left behind; if it holds anything else, take just the
    // image and leave the surrounding text where the author put it.
    const parent = img.parentElement;
    const aloneInParagraph =
      parent?.tagName === 'P' && parent.childNodes.length === 1 && parent.firstChild === img;

    figure.appendChild(img);
    // The caption comes from the markdown image's alt text.
    const caption = img.getAttribute('alt')?.trim();
    if (caption) {
      const figcaption = document.createElement('figcaption');
      figcaption.className = 'trip-attachment-caption muted small';
      figcaption.textContent = caption;
      figure.appendChild(figcaption);
    }

    if (aloneInParagraph) parent.remove();
    img.loading = 'lazy';
    target.appendChild(figure);
  }

  return images.length;
}

/**
 * Pulls costs and attachments out of `content` into their sections, revealing
 * each only when the trip actually has something to put in it.
 *
 * Returns the cost container so the caller can run the cost-table enhancer
 * against its new home rather than the itinerary it no longer lives in.
 */
export function splitTripSections(content: HTMLElement): { costs: HTMLElement | null } {
  stripStaleCostHints(content);

  const costSection = document.getElementById('trip-cost-section');
  const costBody = document.getElementById('trip-cost-body');
  const attachSection = document.getElementById('trip-attachments-section');
  const attachBody = document.getElementById('trip-attachments-body');
  const attachCount = document.getElementById('trip-attachments-count');

  if (costSection && costBody) {
    costSection.hidden = extractCosts(content, costBody) === 0;
  }

  if (attachSection && attachBody) {
    const found = extractAttachments(content, attachBody);
    attachSection.hidden = found === 0;
    if (attachCount) attachCount.textContent = found > 0 ? `(${found})` : '';
  }

  return { costs: costBody };
}
